import { useState, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

type Difficulty = 'Beginner' | 'Intermediate' | 'Advanced';
type ActiveTab = 'practice' | 'heatmap';

interface Practice {
  id: string;
  query: string;
  topic: string;
  difficulty: Difficulty;
}

interface PracticeGeneratorProps {
  currentQuery: string;
  currentInterpretation?: string;
  onSolve: (query: string) => void;
  onClose: () => void;
}

// ─── Practice Stats (localStorage) ───────────────────────────────────────────

const PRACTICE_STATS_KEY = 'gm_practice_v1';
type PracticeStats = Record<string, Record<string, number>>;

function loadPracticeStats(): PracticeStats {
  try {
    const raw = localStorage.getItem(PRACTICE_STATS_KEY);
    return raw ? (JSON.parse(raw) as PracticeStats) : {};
  } catch { return {}; }
}

function incrementPracticeStat(topic: string, difficulty: Difficulty): PracticeStats {
  const stats = loadPracticeStats();
  if (!stats[topic]) stats[topic] = {};
  stats[topic][difficulty] = (stats[topic][difficulty] ?? 0) + 1;
  try { localStorage.setItem(PRACTICE_STATS_KEY, JSON.stringify(stats)); } catch { /* ignore */ }
  return stats;
}

function clearPracticeStats(): void {
  try { localStorage.removeItem(PRACTICE_STATS_KEY); } catch { /* ignore */ }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function fill(t: string): string {
  return t
    .replace(/\bN\b/g, () => randInt(2, 7).toString())
    .replace(/\bM\b/g, () => randInt(1, 5).toString())
    .replace(/\bP\b/g, () => randInt(2, 9).toString());
}

function randNums(count: number, min = 2, max = 20): string {
  const nums = Array.from({ length: count }, () => randInt(min, max));
  return `{${nums.join(', ')}}`;
}

// ─── Problem Templates ────────────────────────────────────────────────────────

const TEMPLATES: Record<string, Record<Difficulty, string[]>> = {
  Derivative: {
    Beginner: ['Derivative of x^N', 'Derivative of x^N + x^M', 'Derivative of Nx^3', 'Derivative of sin(x)', 'Derivative of cos(x)', 'Derivative of e^x', 'Derivative of ln(x)', 'Derivative of x^N - Mx'],
    Intermediate: ['Derivative of x^N * sin(x)', 'Derivative of e^x * cos(x)', 'Derivative of ln(x^2 + N)', 'Derivative of sin(Nx)', 'Derivative of x^N * e^x', 'Derivative of cos(x^N)', 'Derivative of x^M * ln(x)', 'Derivative of sin(x) * cos(x)'],
    Advanced: ['Derivative of sin(x^N) * ln(x)', 'Derivative of (x^N + 1)/(x - M)', 'Derivative of sqrt(x^2 + N)', 'Derivative of x^N * ln(x^M)', 'Derivative of sin(cos(x))', 'Derivative of e^(x^N)', 'Derivative of ln(sin(x))'],
  },
  Integral: {
    Beginner: ['integrate x^N dx from 0 to M', 'integrate sin(x) dx from 0 to 3', 'integrate e^x dx from 0 to N', 'integrate Nx^2 dx from 0 to M', 'integrate x^N + x dx from 1 to M'],
    Intermediate: ['integrate x * e^x dx from 0 to N', 'integrate sin(x)*cos(x) dx from 0 to 3', 'integrate x^2 * sin(x) dx from 0 to N', 'integrate x * sin(Nx) dx from 0 to M', 'integrate x^N * e^x dx from 0 to M'],
    Advanced: ['integrate e^(Nx) * cos(x) dx from 0 to M', 'integrate x^N * ln(x) dx from 1 to M', 'integrate sin(x)^2 dx from 0 to 3', 'integrate x^2 * e^(Nx) dx from 0 to M'],
  },
  Equation: {
    Beginner: ['solve x^2 - Nx + M = 0', 'solve x^2 - N = 0', 'solve 2x + N = M', 'solve x^2 + Nx = 0'],
    Intermediate: ['solve x^2 + Nx - M = 0', 'solve x^3 - Nx = 0', 'solve x^2 - Nx + M = 0', 'solve 2x^2 - Nx + M = 0'],
    Advanced: ['solve x^4 - Nx^2 + M = 0', 'solve x^3 - Nx^2 + Mx = 0', 'solve x^3 - N = 0', 'solve x^4 - Nx^2 = 0'],
  },
  Factor: {
    Beginner: ['factor x^2 - Nx + M', 'factor x^2 - N', 'factor x^2 + Nx + M', 'factor Nx^2 - M'],
    Intermediate: ['factor x^3 - N', 'factor x^3 + Nx^2', 'factor x^3 - Nx^2 + Mx'],
    Advanced: ['factor x^4 - Nx^2 + M', 'factor x^3 - Nx^2 + Mx - P'],
  },
  Statistics: {
    Beginner: [() => `Mean of ${randNums(6)}`, () => `Median of ${randNums(7)}`, () => `Mean of ${randNums(5, 1, 15)}`] as unknown as string[],
    Intermediate: [() => `Standard deviation of ${randNums(6)}`, () => `Variance of ${randNums(5)}`, () => `Mean of ${randNums(8, 5, 30)}`] as unknown as string[],
    Advanced: [() => `Standard deviation of ${randNums(10)}`, () => `Variance of ${randNums(8, 1, 50)}`] as unknown as string[],
  },
  Limit: {
    Beginner: ['limit as x->N of x^2 - M', 'limit as x->M of 2x + N', 'limit as x->N of x^3'],
    Intermediate: ['limit as x->0 of sin(x)/x', 'limit as x->0 of (e^x - 1)/x', 'limit as x->N of (x^2 - M)/(x - P)'],
    Advanced: ['limit as x->0 of (1 - cos(x))/x^2', 'limit as x->0 of (sin(Nx))/(Nx)', 'limit as x->0 of x*ln(x)'],
  },
  Simplify: {
    Beginner: ['simplify (x^2 - N)/(x - M)', 'simplify x^N / x^M', 'simplify (x + N)^2'],
    Intermediate: ['simplify (x^2 + Nx)/(x)', 'simplify (x^N - M)/(x - P)', 'simplify x^2 + Nx + M'],
    Advanced: ['simplify (x^3 - Nx^2 + Mx)/(x)', 'simplify (x^2 - N) * (x + M)'],
  },
  Taylor: {
    Beginner: ['Taylor series of e^x at x=0', 'Taylor series of sin(x) at x=0', 'Taylor series of cos(x) at x=0'],
    Intermediate: ['Taylor series of ln(1+x) at x=0', 'Taylor series of e^(2x) at x=0', 'Taylor series of sin(2x) at x=0'],
    Advanced: ['Taylor series of tan(x) at x=0', 'Taylor series of sqrt(1+x) at x=0'],
  },
};

const ALL_TOPICS = Object.keys(TEMPLATES);
const DIFFICULTIES: Difficulty[] = ['Beginner', 'Intermediate', 'Advanced'];

// ─── Topic Config ─────────────────────────────────────────────────────────────

const TOPIC_ICONS: Record<string, string> = {
  Derivative: 'ri-arrow-up-circle-line',
  Integral: 'ri-function-line',
  Equation: 'ri-equals-line',
  Factor: 'ri-node-tree',
  Statistics: 'ri-bar-chart-2-line',
  Limit: 'ri-arrow-right-line',
  Taylor: 'ri-align-left',
  Simplify: 'ri-scissors-2-line',
};

const DIFF_COLORS: Record<Difficulty, string> = {
  Beginner: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  Intermediate: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  Advanced: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function detectTopic(query: string, interpretation?: string): string {
  const text = (query + ' ' + (interpretation ?? '')).toLowerCase();
  if (/derivative|d\/dx|differentiat/.test(text)) return 'Derivative';
  if (/integrat|∫/.test(text)) return 'Integral';
  if (/solve|equation|roots/.test(text)) return 'Equation';
  if (/\bfactor\b/.test(text)) return 'Factor';
  if (/statistic|mean|std|variance|median/.test(text)) return 'Statistics';
  if (/\blimit\b|lim/.test(text)) return 'Limit';
  if (/\btaylor\b|\bmaclaurin\b|\bseries\b/.test(text)) return 'Taylor';
  if (/simplif/.test(text)) return 'Simplify';
  return 'Equation';
}

function generateProblem(topic: string, difficulty: Difficulty): string {
  const pool = TEMPLATES[topic]?.[difficulty] ?? TEMPLATES['Equation'][difficulty];
  const template = pool[Math.floor(Math.random() * pool.length)];
  if (typeof template === 'function') return (template as () => string)();
  return fill(template);
}

function generateSet(topic: string, difficulty: Difficulty, count: number): Practice[] {
  const seen = new Set<string>();
  const result: Practice[] = [];
  let attempts = 0;
  while (result.length < count && attempts < count * 8) {
    attempts++;
    const q = generateProblem(topic, difficulty);
    if (!seen.has(q)) {
      seen.add(q);
      result.push({ id: `${Date.now()}-${attempts}`, query: q, topic, difficulty });
    }
  }
  return result;
}

// ─── Heatmap Cell Color ───────────────────────────────────────────────────────

function heatColor(count: number): string {
  if (count === 0) return 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-600 border-slate-200 dark:border-slate-700';
  if (count <= 2) return 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/50';
  if (count <= 5) return 'bg-emerald-300 dark:bg-emerald-700/70 text-emerald-900 dark:text-emerald-100 border-emerald-400 dark:border-emerald-600';
  if (count <= 10) return 'bg-emerald-500 dark:bg-emerald-600 text-white border-emerald-600 dark:border-emerald-500';
  return 'bg-emerald-700 dark:bg-emerald-500 text-white border-emerald-800 dark:border-emerald-400';
}

// ─── Heatmap View ─────────────────────────────────────────────────────────────

function HeatmapView({ stats, onReset }: { stats: PracticeStats; onReset: () => void }) {
  const [hoveredCell, setHoveredCell] = useState<{ topic: string; diff: Difficulty } | null>(null);
  const totalSolved = ALL_TOPICS.reduce((sum, t) =>
    sum + DIFFICULTIES.reduce((s2, d) => s2 + (stats[t]?.[d] ?? 0), 0), 0
  );
  const topicTotals = ALL_TOPICS.map(t => ({
    topic: t,
    total: DIFFICULTIES.reduce((s, d) => s + (stats[t]?.[d] ?? 0), 0),
  })).sort((a, b) => b.total - a.total);

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Summary line */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Practice Activity</h3>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{totalSolved} total problems attempted</p>
        </div>
        {totalSolved > 0 && (
          <button
            onClick={onReset}
            className="text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-delete-bin-5-line mr-1"></i>
            Reset Stats
          </button>
        )}
      </div>

      {/* Grid */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-1.5">
          <thead>
            <tr>
              <th className="text-left text-xs font-bold text-slate-500 dark:text-slate-400 pb-2 pr-3 w-24">Topic</th>
              {DIFFICULTIES.map(d => (
                <th key={d} className="text-center pb-2">
                  <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-bold ${DIFF_COLORS[d]}`}>{d}</span>
                </th>
              ))}
              <th className="text-center pb-2 pl-2 text-xs font-bold text-slate-500 dark:text-slate-400">Total</th>
            </tr>
          </thead>
          <tbody>
            {ALL_TOPICS.map(topic => {
              const rowTotal = DIFFICULTIES.reduce((s, d) => s + (stats[topic]?.[d] ?? 0), 0);
              return (
                <tr key={topic}>
                  <td className="pr-3 py-1">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${TOPIC_ICONS[topic] ?? 'ri-function-line'} text-emerald-600 dark:text-emerald-400 text-xs`}></i>
                      </div>
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">{topic}</span>
                    </div>
                  </td>
                  {DIFFICULTIES.map(diff => {
                    const count = stats[topic]?.[diff] ?? 0;
                    const isHovered = hoveredCell?.topic === topic && hoveredCell?.diff === diff;
                    return (
                      <td key={diff} className="text-center py-1">
                        <div className="flex justify-center">
                          <div
                            className={`relative w-16 h-10 rounded-xl border-2 flex items-center justify-center cursor-default transition-all duration-150 font-bold text-sm select-none ${heatColor(count)} ${isHovered ? 'scale-110' : ''}`}
                            onMouseEnter={() => setHoveredCell({ topic, diff })}
                            onMouseLeave={() => setHoveredCell(null)}
                          >
                            {count === 0 ? (
                              <span className="text-[10px] font-normal opacity-50">—</span>
                            ) : (
                              <span>{count}</span>
                            )}
                            {/* Tooltip */}
                            {isHovered && count > 0 && (
                              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[10px] rounded-lg px-2.5 py-2 whitespace-nowrap z-20 pointer-events-none shadow-xl">
                                <div className="font-bold">{count} attempt{count !== 1 ? 's' : ''}</div>
                                <div className="text-slate-300 dark:text-slate-600">{topic} — {diff}</div>
                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900 dark:border-t-white"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  })}
                  <td className="text-center py-1 pl-2">
                    <span className={`text-sm font-bold ${rowTotal > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-600'}`}>
                      {rowTotal > 0 ? rowTotal : '—'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className="pt-3 text-xs font-bold text-slate-500 dark:text-slate-400 pr-3">Total</td>
              {DIFFICULTIES.map(diff => {
                const diffTotal = ALL_TOPICS.reduce((s, t) => s + (stats[t]?.[diff] ?? 0), 0);
                return (
                  <td key={diff} className="pt-3 text-center">
                    <span className={`text-sm font-black ${diffTotal > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-600'}`}>
                      {diffTotal > 0 ? diffTotal : '—'}
                    </span>
                  </td>
                );
              })}
              <td className="pt-3 text-center pl-2">
                <span className={`text-sm font-black ${totalSolved > 0 ? 'text-slate-800 dark:text-white' : 'text-slate-400'}`}>{totalSolved}</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs font-bold text-slate-500 dark:text-slate-400">Heat scale:</span>
        {[
          { label: '0', color: 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700' },
          { label: '1–2', color: 'bg-emerald-100 dark:bg-emerald-900/40 border-emerald-200 dark:border-emerald-700/50' },
          { label: '3–5', color: 'bg-emerald-300 dark:bg-emerald-700/70 border-emerald-400 dark:border-emerald-600' },
          { label: '6–10', color: 'bg-emerald-500 dark:bg-emerald-600 border-emerald-600 dark:border-emerald-500' },
          { label: '11+', color: 'bg-emerald-700 dark:bg-emerald-500 border-emerald-800 dark:border-emerald-400' },
        ].map(({ label, color }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`w-6 h-6 rounded-lg border-2 ${color}`}></div>
            <span className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold">{label}</span>
          </div>
        ))}
      </div>

      {/* Top topics */}
      {totalSolved > 0 && (
        <div>
          <h4 className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">Most Practiced</h4>
          <div className="space-y-2">
            {topicTotals.filter(t => t.total > 0).slice(0, 4).map(({ topic, total }, i) => {
              const pct = Math.round((total / totalSolved) * 100);
              return (
                <div key={topic} className="flex items-center gap-3">
                  <span className="text-xs text-slate-500 dark:text-slate-400 w-4 text-right font-bold">#{i + 1}</span>
                  <div className="flex items-center gap-1.5 w-24">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className={`${TOPIC_ICONS[topic] ?? 'ri-function-line'} text-emerald-600 dark:text-emerald-400 text-[10px]`}></i>
                    </div>
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">{topic}</span>
                  </div>
                  <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    ></div>
                  </div>
                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 w-8 text-right">{total}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {totalSolved === 0 && (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
            <i className="ri-grid-line text-3xl text-slate-400"></i>
          </div>
          <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">No practice data yet</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Switch to the Practice tab and start solving problems!</p>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PracticeGenerator({ currentQuery, currentInterpretation, onSolve, onClose }: PracticeGeneratorProps) {
  const detectedTopic = detectTopic(currentQuery, currentInterpretation);
  const [activeTab, setActiveTab] = useState<ActiveTab>('practice');
  const [topic, setTopic] = useState(detectedTopic);
  const [difficulty, setDifficulty] = useState<Difficulty>('Intermediate');
  const [count, setCount] = useState(5);
  const [problems, setProblems] = useState<Practice[]>(() => generateSet(detectedTopic, 'Intermediate', 5));
  const [solvedIds, setSolvedIds] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState<PracticeStats>(loadPracticeStats);

  const handleGenerate = useCallback(() => {
    setSolvedIds(new Set());
    setProblems(generateSet(topic, difficulty, count));
  }, [topic, difficulty, count]);

  const handleSolve = (p: Practice) => {
    setSolvedIds(prev => new Set([...prev, p.id]));
    const newStats = incrementPracticeStat(p.topic, p.difficulty);
    setStats(newStats);
    onSolve(p.query);
  };

  const handleReset = () => {
    clearPracticeStats();
    setStats({});
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-7 py-5 flex items-start justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
              <i className="ri-sparkling-2-line text-white text-lg"></i>
            </div>
            <div>
              <div className="font-bold text-white text-base">Practice Generator</div>
              <div className="text-emerald-100 text-xs">Detected topic: {detectedTopic}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors text-white cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex-shrink-0">
          {([
            { key: 'practice', label: 'Practice', icon: 'ri-list-check-3' },
            { key: 'heatmap', label: 'Heatmap', icon: 'ri-grid-fill' },
          ] as { key: ActiveTab; label: string; icon: string }[]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-bold transition-colors cursor-pointer border-b-2 ${
                activeTab === tab.key
                  ? 'border-emerald-500 text-emerald-700 dark:text-emerald-400 bg-white dark:bg-slate-900'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
            >
              <i className={`${tab.icon} text-xs`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'practice' && (
          <>
            {/* Controls */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30 flex-shrink-0">
              <div className="grid grid-cols-3 gap-4">
                {/* Topic */}
                <div>
                  <label className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide block mb-2">Topic</label>
                  <div className="flex flex-wrap gap-1">
                    {ALL_TOPICS.map(t => (
                      <button
                        key={t}
                        onClick={() => setTopic(t)}
                        className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer whitespace-nowrap ${
                          topic === t ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-emerald-400'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Difficulty */}
                <div>
                  <label className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide block mb-2">Difficulty</label>
                  <div className="flex flex-col gap-1">
                    {DIFFICULTIES.map(d => (
                      <button
                        key={d}
                        onClick={() => setDifficulty(d)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap text-left ${
                          difficulty === d ? DIFF_COLORS[d] : 'bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-emerald-400'
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Count + Generate */}
                <div>
                  <label className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide block mb-2">Count</label>
                  <div className="flex gap-1.5 mb-3">
                    {[3, 5, 10].map(n => (
                      <button
                        key={n}
                        onClick={() => setCount(n)}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                          count === n ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={handleGenerate}
                    className="w-full py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line mr-1"></i>
                    Generate New Set
                  </button>
                </div>
              </div>
            </div>

            {/* Problem List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
                {problems.length} {difficulty} {topic} Problems
              </div>
              {problems.map((p, i) => {
                const solved = solvedIds.has(p.id);
                const topicCount = stats[p.topic]?.[p.difficulty] ?? 0;
                return (
                  <div
                    key={p.id}
                    className={`rounded-xl border-2 p-4 transition-all ${
                      solved
                        ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/20'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-emerald-300 dark:hover:border-emerald-600'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-bold ${solved ? 'bg-emerald-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                        {solved ? <i className="ri-check-line text-sm"></i> : i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className={`${TOPIC_ICONS[p.topic] ?? 'ri-function-line'} text-emerald-600 dark:text-emerald-400 text-xs`}></i>
                          </div>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${DIFF_COLORS[p.difficulty]}`}>{p.difficulty}</span>
                          {topicCount > 0 && (
                            <span className="text-[9px] text-slate-400 dark:text-slate-500 font-semibold">
                              {topicCount} practiced
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-mono text-slate-800 dark:text-slate-100 font-semibold">{p.query}</p>
                      </div>
                      <button
                        onClick={() => handleSolve(p)}
                        className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                          solved
                            ? 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                            : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 shadow-sm'
                        }`}
                      >
                        <i className={solved ? 'ri-refresh-line' : 'ri-play-circle-line'}></i>
                        {solved ? 'Retry' : 'Solve It'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 pt-3 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
              <div className="text-xs text-slate-400 dark:text-slate-500">{solvedIds.size} of {problems.length} solved</div>
              <div className="flex gap-2">
                <button onClick={handleGenerate} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 text-emerald-700 dark:text-emerald-300 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-refresh-line"></i>New Set
                </button>
                <button onClick={onClose} className="px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
                  Close
                </button>
              </div>
            </div>
          </>
        )}

        {activeTab === 'heatmap' && (
          <>
            <HeatmapView stats={stats} onReset={handleReset} />
            <div className="px-6 pb-5 pt-3 border-t border-slate-100 dark:border-slate-700 flex items-center justify-end flex-shrink-0">
              <button onClick={onClose} className="px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
